import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
  OnInit
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

import { ServiceRoomJourneyVO, SessionJourneyVO, SessionMessageVO } from '../tenant-contact.types';

@Component({
  selector: 'tenant-contact-journey',
  standalone: true,
  templateUrl: './tenant-contact-journey.component.html',
  styleUrls: ['./tenant-contact-journey.component.less'],
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzDatePickerModule,
    NzEmptyModule,
    NzIconModule,
    NzPaginationModule,
    NzSpinModule,
    NzTagModule,
    NzToolTipModule,
    TestIdDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantContactJourneyComponent implements OnInit {
  private readonly sessionEventTranslationKeys: Record<string, string> = {
    SessionStart: 'tenant.contact.session-event.session-start',
    AgentStart: 'tenant.contact.session-event.agent-start',
    AgentStop: 'tenant.contact.session-event.agent-stop',
    CustomerStop: 'tenant.contact.session-event.customer-stop',
    Timeout: 'tenant.contact.session-event.timeout',
    RobotActive: 'tenant.contact.session-event.robot-active',
    RobotStop: 'tenant.contact.session-event.robot-stop',
    SessionAutoTransfer: 'tenant.contact.session-event.session-auto-transfer',
    SessionMakeTransfer: 'tenant.contact.session-event.session-make-transfer',
    ConsultStart: 'tenant.contact.session-event.consult-start',
    ConsultStop: 'tenant.contact.session-event.consult-stop',
    TransferStart: 'tenant.contact.session-event.transfer-start',
    TransferCancel: 'tenant.contact.session-event.transfer-cancel',
    TransferComplete: 'tenant.contact.session-event.transfer-complete'
  };
  @ViewChild('messageContainer') private messageContainer?: ElementRef<HTMLElement>;
  @Input({ required: true }) tenantId!: string;
  @Input({ required: true }) contactId!: string;
  @Input() customerAccountId?: string;
  @Input() roomOnly = false;
  @Input() initialRoom?: ServiceRoomJourneyVO;
  @Output() readonly roomSelected = new EventEmitter<ServiceRoomJourneyVO>();
  private readonly modalData = inject(NZ_MODAL_DATA, { optional: true }) as Partial<{
    tenantId: string;
    contactId: string;
    customerAccountId: string;
    roomOnly: boolean;
    initialRoom: ServiceRoomJourneyVO;
  }> | null;
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly toast = inject(NzMessageService);
  readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  rooms: ServiceRoomJourneyVO[] = [];
  sessions: SessionJourneyVO[] = [];
  messages: SessionMessageVO[] = [];
  selectedRoom?: ServiceRoomJourneyVO;
  selectedSession?: SessionJourneyVO;
  range: Date[] = [];
  loadingRooms = false;
  loadingSessions = false;
  loadingMessages = false;
  roomPage = 1;
  sessionPage = 1;
  roomTotal = 0;
  sessionTotal = 0;
  readonly roomPageSize = 8;
  readonly sessionPageSize = 10;
  hasOlderMessages = true;
  hasNewerMessages = true;

  ngOnInit(): void {
    if (this.modalData) {
      this.tenantId = this.modalData.tenantId ?? this.tenantId;
      this.contactId = this.modalData.contactId ?? this.contactId;
      this.customerAccountId = this.modalData.customerAccountId ?? this.customerAccountId;
      this.roomOnly = this.modalData.roomOnly ?? this.roomOnly;
      this.initialRoom = this.modalData.initialRoom ?? this.initialRoom;
    }
    if (this.initialRoom) {
      this.selectedRoom = this.initialRoom;
      if (!this.roomOnly) this.loadSessions(this.initialRoom);
    } else if (this.roomOnly) this.loadRooms();
  }
  onRangeChange(): void {
    this.roomPage = 1;
    this.sessionPage = 1;
    if (this.roomOnly || !this.selectedRoom) {
      this.loadRooms();
      return;
    }
    this.loadSessions(this.selectedRoom);
  }
  loadRooms(): void {
    this.loadingRooms = true;
    this.http
      .post(ConstantUtil.TENANT_CONTACT_JOURNEY_SERVICE_ROOM_LIST, {
        tenantId: this.tenantId,
        contactId: this.contactId,
        pageIndex: this.roomPage - 1,
        pageSize: this.roomPageSize,
        ...this.timeRange()
      })
      .pipe(
        finalize(() => {
          this.loadingRooms = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(res => {
        if (!res.success) {
          this.toast.error(res.msg || '載入服務號失敗');
          return;
        }
        const data = res.data || {};
        this.rooms = data.items || [];
        this.roomTotal = data.totalCount || 0;
        if (!this.roomOnly && !this.selectedRoom && this.rooms[0]) this.selectRoom(this.rooms[0]);
      });
  }
  selectRoom(room: ServiceRoomJourneyVO): void {
    this.selectedRoom = room;
    this.roomSelected.emit(room);
    if (!this.roomOnly) {
      this.sessionPage = 1;
      this.loadSessions(room);
    }
  }
  loadSessions(room: ServiceRoomJourneyVO): void {
    this.loadingSessions = true;
    this.http
      .post(ConstantUtil.TENANT_CONTACT_JOURNEY_SESSION_LIST, {
        tenantId: this.tenantId,
        contactId: this.contactId,
        roomId: room.roomId,
        pageIndex: this.sessionPage - 1,
        pageSize: this.sessionPageSize,
        ...this.timeRange()
      })
      .pipe(
        finalize(() => {
          this.loadingSessions = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(res => {
        if (!res.success) {
          this.toast.error(res.msg || '載入 Session 失敗');
          return;
        }
        const data = res.data || {};
        this.sessions = data.items || [];
        this.sessionTotal = data.totalCount || 0;
        this.messages = [];
        this.selectedSession = undefined;
        if (this.sessions[0]) this.selectSession(this.sessions[0]);
      });
  }
  selectSession(session: SessionJourneyVO): void {
    this.selectedSession = session;
    this.messages = [];
    this.hasOlderMessages = false;
    this.hasNewerMessages = true;
    this.loadMessages('asc', 0);
  }
  loadMessages(direction: 'asc' | 'desc', sequence: number): void {
    if (
      !this.selectedRoom ||
      !this.selectedSession ||
      this.loadingMessages ||
      (direction === 'desc' && !this.hasOlderMessages) ||
      (direction === 'asc' && sequence > 0 && !this.hasNewerMessages)
    )
      return;
    const messageContainer = this.messageContainer?.nativeElement;
    const previousHeight = messageContainer?.scrollHeight ?? 0;
    const previousTop = messageContainer?.scrollTop ?? 0;
    this.loadingMessages = true;
    this.http
      .post(ConstantUtil.TENANT_CONTACT_JOURNEY_MESSAGE_LIST, {
        tenantId: this.tenantId,
        contactId: this.contactId,
        roomId: this.selectedRoom.roomId,
        sessionId: this.selectedSession.sessionId,
        direction,
        sequence,
        pageSize: 50
      })
      .pipe(
        finalize(() => {
          this.loadingMessages = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(res => {
        if (!res.success) {
          this.toast.error(res.msg || '載入消息失敗');
          return;
        }
        const data = res.data || {};
        const incoming: SessionMessageVO[] = data.items || [];
        const merged = [...this.messages, ...incoming].filter((item, index, all) => all.findIndex(other => other.id === item.id) === index);
        this.messages = merged.sort((a, b) => a.sequence - b.sequence);
        if (direction === 'desc') this.hasOlderMessages = Boolean(data.hasNextPage) && incoming.length > 0;
        else {
          this.hasNewerMessages = Boolean(data.hasNextPage) && incoming.length > 0;
          if (sequence === 0) this.hasOlderMessages = false;
        }
        if (direction === 'desc' && incoming.length && messageContainer) {
          requestAnimationFrame(() => {
            messageContainer.scrollTop = previousTop + messageContainer.scrollHeight - previousHeight;
          });
        }
      });
  }
  onMessageScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!this.messages.length || this.loadingMessages) return;
    if (el.scrollTop < 24) this.loadMessages('desc', this.messages[0].sequence);
    else if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) this.loadMessages('asc', this.messages.at(-1)?.sequence || 0);
  }
  formatTime(value?: number): string {
    return value ? new Date(value).toLocaleString() : '-';
  }
  isStructuredMessage(content?: string): boolean {
    if (!content) return false;
    try {
      return typeof JSON.parse(content) === 'object';
    } catch {
      return false;
    }
  }
  getMessagePreview(content?: string): string {
    if (!content) return '-';
    try {
      const data = JSON.parse(content) as Record<string, unknown>;
      if (typeof data['text'] === 'string') return data['text'];
      if (typeof data['title'] === 'string') {
        return [data['title'], data['subTitle'], data['text']]
          .filter((value, index, all) => typeof value === 'string' && all.indexOf(value) === index)
          .join(' · ');
      }
      if (typeof data['event'] === 'string') return this.translateSessionEvent(data['event']);
      if (typeof data['name'] === 'string') return `附件：${data['name']}`;
      return content;
    } catch {
      return content;
    }
  }

  getSessionStatusText(status?: string): string {
    if (!status) return '-';
    const key = this.sessionEventTranslationKeys[status];
    return key ? this.i18n.fanyi(key) : status;
  }
  /** 將後端會話事件碼轉換為 AileAdmin 可讀的多語言文案，未知事件保留原始值。 */
  private translateSessionEvent(event: string): string {
    const translationKey = this.sessionEventTranslationKeys[event];
    return translationKey ? this.i18n.fanyi(translationKey) : event;
  }
  getMessageRole(message: SessionMessageVO): 'customer' | 'agent' | 'robot' | 'system' {
    const normalized = String(message.sourceType ?? '').toLowerCase();
    if (normalized.includes('system')) return 'system';
    if (normalized.includes('assistant') || normalized.includes('robot') || normalized.includes('bot') || normalized === 'ai') {
      return 'robot';
    }
    if (normalized.includes('customer')) return 'customer';

    const isCustomerSender =
      Boolean(message.senderId && message.senderId === this.contactId) ||
      Boolean(message.accountId && this.customerAccountId && message.accountId === this.customerAccountId);
    return isCustomerSender ? 'customer' : 'agent';
  }
  getMessageRoleLabel(message: SessionMessageVO): string {
    const role = this.getMessageRole(message);
    return ({ customer: '客戶', agent: '客服', robot: '機器人', system: '系統' } as const)[role];
  }
  private timeRange(): { sessionStartTimeFrom?: number; sessionStartTimeTo?: number } {
    const result: { sessionStartTimeFrom?: number; sessionStartTimeTo?: number } = {};
    if (this.range[0]) result.sessionStartTimeFrom = new Date(this.range[0]).setHours(0, 0, 0, 0);
    if (this.range[1]) result.sessionStartTimeTo = new Date(this.range[1]).setHours(23, 59, 59, 999);
    return result;
  }
}
